import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// Colour-mode preference. Three states rather than a boolean so "System" is a
// real, sticky choice: a user who has never touched this follows the OS, and a
// user who explicitly picks Light keeps Light even after the OS flips at dusk.
//
// The mechanics live in styles/tokens.css (generated from dyuthi-design):
//   - no attribute        -> the `prefers-color-scheme` media block decides
//   - data-theme="dark"   -> forced dark
//   - data-theme="light"  -> forced light (the media block explicitly excludes it)
// So all this store has to do is stamp the attribute on <html>.

export type ColorMode = "system" | "light" | "dark";

type ThemeState = {
  mode: ColorMode;
  setMode: (mode: ColorMode) => void;
};

/** Reflects the preference onto the document so CSS can act on it. */
function applyMode(mode: ColorMode) {
  const root = document.documentElement;
  if (mode === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", mode);
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: "system",
      setMode: (mode) => {
        applyMode(mode);
        set({ mode });
      },
    }),
    {
      name: "dyuthi-theme",
      storage: createJSONStorage(() => localStorage),
      // Persisted state is read back *after* first paint, so re-apply here.
      // `initTheme()` covers the pre-paint case; this covers the rehydrate.
      onRehydrateStorage: () => (state) => {
        if (state) applyMode(state.mode);
      },
    },
  ),
);

/**
 * Applies the stored preference before React mounts, so a user on forced-dark
 * never sees a white flash. Reads localStorage directly rather than waiting for
 * zustand to rehydrate — this runs from main.tsx as the very first statement.
 */
export function initTheme() {
  try {
    const raw = localStorage.getItem("dyuthi-theme");
    const mode: ColorMode = raw ? JSON.parse(raw)?.state?.mode ?? "system" : "system";
    applyMode(mode);
  } catch {
    // Private mode / disabled storage: fall back to following the OS.
    applyMode("system");
  }
}

/** The mode actually in effect right now, resolving "system" against the OS. */
export function resolvedMode(mode: ColorMode): "light" | "dark" {
  if (mode !== "system") return mode;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
